/**
 * Seeds the plantSpecies catalog from the plant-inventory data.
 * Deduplicates by scientific name, taking the first occurrence's metadata.
 */
import sharedDb from './db.js';
import { v4 as uuidv4 } from 'uuid';
import { recordChange } from './syncService.js';

let plantInventoryData = null;
try {
  plantInventoryData = require('./data/plantInventory.json');
} catch (e) {
  // File may not exist at runtime — seedSpeciesFromInventory will return 0
  plantInventoryData = null;
}

/**
 * Extract scientific name from the "name" field by stripping container/size info.
 * e.g., "Asclepias incarnata (Medium Plug)" -> "Asclepias incarnata"
 *       "Andropogon gerardii LE (Medium Plug)" -> "Andropogon gerardii"
 *       "Sambucus canadensis LE S (Large Plug)" -> "Sambucus canadensis"
 *       "Myrica cerifera LEAAC (Community Pot)" -> "Myrica cerifera"
 */
function extractScientificName(name) {
  // Remove anything in parentheses at the end
  let scientific = name.replace(/\s*\([^)]*\)\s*$/, '').trim();
  // Remove trailing source/provenance codes — these are sequences of ALL-UPPERCASE words
  // at the end of the string (e.g., "LE", "LECHC", "LE S", "LEAAC", "RE")
  // Scientific names use lowercase after the genus, so uppercase-only trailing words are codes.
  scientific = scientific.replace(/(\s+[A-Z][A-Z0-9]*)+\s*$/, '').trim();
  // Also handle single uppercase letter codes like "S" that follow other codes
  // by re-running the strip (handles "LE S" -> strips "LE" then "S" is left)
  scientific = scientific.replace(/(\s+[A-Z][A-Z0-9]*)+\s*$/, '').trim();
  // Handle edge case: trailing single uppercase letter (e.g., leftover "S")
  scientific = scientific.replace(/\s+[A-Z]\s*$/, '').trim();
  return scientific;
}

/**
 * Seed the species catalog. Only adds species that don't already exist.
 * Returns the number of new species added.
 */
export async function seedSpeciesFromInventory() {
  try {
    if (!plantInventoryData || !Array.isArray(plantInventoryData)) {
      return 0;
    }

    // Deduplicate by scientific name
    const speciesMap = new Map();

    for (const plant of plantInventoryData) {
      const scientificName = extractScientificName(plant.name);
      if (!scientificName) continue;

      if (!speciesMap.has(scientificName.toLowerCase())) {
        speciesMap.set(scientificName.toLowerCase(), {
          commonName: plant.common || '',
          scientificName,
          lightRequirements: plant.light || '',
          moistureRequirements: plant.moisture || '',
          soilRequirements: plant.soil || '',
          bloomSeason: plant.bloom || '',
          bloomColor: plant.color || '',
          agcpRating: null,
          empRating: null,
        });
      }
    }

    // Check existing species to avoid duplicates
    const existingSpecies = await sharedDb.plantSpecies.toArray();
    const existingNames = new Set(existingSpecies.map(s => s.scientificName.toLowerCase()));

    const now = new Date().toISOString();
    let added = 0;

    for (const [key, speciesData] of speciesMap) {
      if (existingNames.has(key)) continue;

      const id = uuidv4();
      await sharedDb.plantSpecies.put({
        ...speciesData,
        id,
        createdAt: now,
        updatedAt: now,
      });
      recordChange('plantSpecies', id, 'put');
      added++;
    }

    return added;
  } catch (e) {
    // If the file is missing or malformed, return 0 without throwing
    return 0;
  }
}

/**
 * Clear all species and re-seed from inventory.
 */
export async function clearAndReseedSpecies() {
  await sharedDb.plantSpecies.clear();
  return seedSpeciesFromInventory();
}
